import { Agent_SortField, Agent_SortOrder } from '@/graphql/codegen/generated/graphql';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { computed, ComputedRef, inject } from 'vue';
import { app } from '@/bootloader';
import { useOsService } from '@/services/OsService/OsService';
import { IKAgentsTableConstants } from './agents-table.InjectionKeys';
import {
  AgentsTableColumnsWindowsManagement,
  DefaultTableWindowsColumns,
  MapAgentSortFieldToColumnsWindows,
  MapColumnsToAgentSortFieldWindows,
  NonSortableWindowsColumns,
  SortableWindowsColumns,
} from './types/OS/agents-table.windows';
import {
  DefaultTableLinuxColumns,
  MapAgentSortFieldToColumnsLinux,
  MapColumnsToAgentSortFieldLinux,
  NonSortableLinuxColumns,
  SortableLinuxColumns,
  AgentsTableColumnsLinuxManagement,
} from './types/OS/agents-table.linux';
import { DefaultTableColumns, MapAgentSortFieldToColumns, NonSortableColumns, SortableColumns, TableColumns } from './types/agents-table.types';

/* Mapping the `AgentsTableWindowsColumns` to the `Agent_SortField for Agents GQL query` */

const MAP_COLUMNS_To_Agent_SortField = (os: OsType) => {
  const columnsToAgentWindows = {
    'Agent Description': Agent_SortField.HostComment,
    Alerts: Agent_SortField.Alerts,
    'Available CPU': Agent_SortField.AvailableCpu,
    'Available Memory': Agent_SortField.AvailableMemory,
    'Free Disk Space': Agent_SortField.AvailableDiskSpace,
    'Build Cache Allocated': Agent_SortField.BuildCache,
    'Build Group': Agent_SortField.BuildGroup,
    'Build Priority': Agent_SortField.BuildPriority,
    'CPU Info': Agent_SortField.CpuInfo,
    'Last Connected': Agent_SortField.LastConnected,
    'Logged On User': Agent_SortField.LoggedOnUser,
    'Log Level': Agent_SortField.LogLevel,
    'Logical Cores': Agent_SortField.LogicalCores,
    'Agent Name': Agent_SortField.Name,
    Network: Agent_SortField.Network,
    'MAC Address': Agent_SortField.MacAddress,
    Status: Agent_SortField.Status,
    'Used Helpers Cache': Agent_SortField.UsedHelperCache,
    'Assignment Priority': Agent_SortField.AssignPriority,
    'Registered Helper Cores': Agent_SortField.RegisteredCores,
    IP: Agent_SortField.Ip,
    'Operating System': Agent_SortField.OsVersion,
    'Physical Cores': Agent_SortField.PhysicalCores,
    Port: Agent_SortField.Port,
    'Routing IP': Agent_SortField.RoutingIp,
    'Up/Down time': Agent_SortField.Uptime,
    'VM Type': Agent_SortField.VmType,
    Spot: Agent_SortField.Spot,
    'HD Type': Agent_SortField.HdType,
    'Volume Size': Agent_SortField.VolumeSize,
    'Cache Size': Agent_SortField.CacheSize,
    'Availability Zone': Agent_SortField.Zone,
    'Cloud Cores': Agent_SortField.NumberOfCores,
    'Build Cache Endpoint': Agent_SortField.CacheSettingsDisplay,
    'Cache Cluster': Agent_SortField.Cluster,
  };
  const sortableColumnsLinux = {
    'Agent Description': Agent_SortField.HostComment,
    'Agent Name': Agent_SortField.Name,
    'Available Memory': Agent_SortField.AvailableMemory,
    'Build Group': Agent_SortField.BuildGroup,
    Status: Agent_SortField.Status,
    IP: Agent_SortField.Ip,
    'Operating System': Agent_SortField.OsVersion,
    Subscription: Agent_SortField.LicenseStatus,
    'Last Connected': Agent_SortField.LastConnected,
    'CPU Load': Agent_SortField.UsedCpu,
    'Subscribed Cores/Total': Agent_SortField.RegisteredCores,
    Version: Agent_SortField.Version,
    'Initiator/Helper Active Cores': Agent_SortField.InitiatorCores,
    'Assigned To Initiator': Agent_SortField.AssignedToInitiator,
    'Model Name': Agent_SortField.CpuInfo,
    'CPU Frequency': Agent_SortField.CpuSpeed,
    'VM Type': Agent_SortField.VmType,
    Spot: Agent_SortField.Spot,
    'HD Type': Agent_SortField.HdType,
    'Volume Size': Agent_SortField.VolumeSize,
    'Cache Size': Agent_SortField.CacheSize,
    'Availability Zone': Agent_SortField.Zone,
    'Cloud Cores': Agent_SortField.NumberOfCores,
  };
  const result: {
    [OsType.Windows]: MapColumnsToAgentSortFieldWindows;
    [OsType.Linux]: MapColumnsToAgentSortFieldLinux;
    [OsType.Mac]: MapColumnsToAgentSortFieldLinux;
  } = {
    [OsType.Windows]: columnsToAgentWindows,
    [OsType.Linux]: sortableColumnsLinux,
    [OsType.Mac]: sortableColumnsLinux,
  };
  return result[os];
};

const MAP_Agent_SortField_To_COLUMNS = <T extends OsType>(os: T): MapAgentSortFieldToColumns[T] => {
  const agentSortFieldToColumnsWindows: MapAgentSortFieldToColumnsWindows = {
    [Agent_SortField.HostComment]: 'Agent Description',
    [Agent_SortField.Alerts]: 'Alerts',
    [Agent_SortField.AvailableCpu]: 'Available CPU',
    [Agent_SortField.AvailableMemory]: 'Available Memory',
    [Agent_SortField.BuildGroup]: 'Build Group',
    [Agent_SortField.LastConnected]: 'Last Connected',
    [Agent_SortField.LoggedOnUser]: 'Logged On User',
    [Agent_SortField.Name]: 'Agent Name',
    [Agent_SortField.Network]: 'Network',
    [Agent_SortField.Status]: 'Status',
    [Agent_SortField.UsedHelperCache]: 'Used Helpers Cache',
    [Agent_SortField.AssignPriority]: 'Assignment Priority',
    [Agent_SortField.RegisteredCores]: 'Registered Helper Cores',
    [Agent_SortField.Ip]: 'IP',
    [Agent_SortField.AvailableDiskSpace]: 'Free Disk Space',
    [Agent_SortField.BuildCache]: 'Build Cache Allocated',
    [Agent_SortField.BuildPriority]: 'Build Priority',
    [Agent_SortField.CpuInfo]: 'CPU Info',
    [Agent_SortField.LogLevel]: 'Log Level',
    [Agent_SortField.LogicalCores]: 'Logical Cores',
    [Agent_SortField.MacAddress]: 'MAC Address',
    [Agent_SortField.OsVersion]: 'Operating System',
    [Agent_SortField.PhysicalCores]: 'Physical Cores',
    [Agent_SortField.Port]: 'Port',
    [Agent_SortField.RoutingIp]: 'Routing IP',
    [Agent_SortField.Uptime]: 'Up/Down time',
    [Agent_SortField.InitiatorCores]: 'Log Level',
    [Agent_SortField.AssignedToInitiator]: 'Log Level',
    [Agent_SortField.CpuSpeed]: 'Log Level',
    [Agent_SortField.HelperCores]: 'Log Level',
    [Agent_SortField.LicenseStatus]: 'Log Level',
    [Agent_SortField.Version]: 'Log Level',
    [Agent_SortField.UsedCpu]: 'Log Level',
    [Agent_SortField.VmType]: 'VM Type',
    [Agent_SortField.Spot]: 'Spot',
    [Agent_SortField.HdType]: 'HD Type',
    [Agent_SortField.VolumeSize]: 'Volume Size',
    [Agent_SortField.CacheSize]: 'Cache Size',
    [Agent_SortField.Zone]: 'Availability Zone',
    [Agent_SortField.NumberOfCores]: 'Cloud Cores',
    [Agent_SortField.CacheSettingsDisplay]: 'Build Cache Endpoint',
    [Agent_SortField.Cluster]: 'Cache Cluster',
  };
  const agentSortFieldToColumnsLinux: MapAgentSortFieldToColumnsLinux = {
    [Agent_SortField.Alerts]: 'Agent Name',
    [Agent_SortField.AvailableCpu]: 'Agent Name', // not exists in linux
    [Agent_SortField.HostComment]: 'Agent Description',
    [Agent_SortField.AvailableMemory]: 'Available Memory',
    [Agent_SortField.BuildGroup]: 'Build Group',
    [Agent_SortField.LastConnected]: 'Last Connected',
    [Agent_SortField.LoggedOnUser]: 'Agent Name', // not exists in linux
    [Agent_SortField.Name]: 'Agent Name', // not exists in linux
    [Agent_SortField.Network]: 'Agent Name', // not exists in linux
    [Agent_SortField.Status]: 'Status',
    [Agent_SortField.UsedHelperCache]: 'Agent Name', // not exists in linux
    [Agent_SortField.AssignPriority]: 'Agent Name', // not exists in linux
    [Agent_SortField.RegisteredCores]: 'Subscribed Cores/Total',
    [Agent_SortField.Ip]: 'IP',
    [Agent_SortField.AvailableDiskSpace]: 'Agent Name', // not exists in linux
    [Agent_SortField.BuildCache]: 'Agent Name', // not exists in linux
    [Agent_SortField.BuildPriority]: 'Agent Name', // not exists in linux
    [Agent_SortField.LogLevel]: 'Agent Name', // not exists in linux
    [Agent_SortField.LogicalCores]: 'Agent Name',
    [Agent_SortField.MacAddress]: 'Agent Name', // not exists in linux
    [Agent_SortField.OsVersion]: 'Operating System',
    [Agent_SortField.PhysicalCores]: 'Agent Name', // not exists in linux
    [Agent_SortField.Port]: 'Agent Name', // not exists in linux
    [Agent_SortField.RoutingIp]: 'Agent Name', // not exists in linux
    [Agent_SortField.Uptime]: 'Agent Name', // not exists in linux
    [Agent_SortField.InitiatorCores]: 'Initiator/Helper Active Cores',
    [Agent_SortField.AssignedToInitiator]: 'Assigned To Initiator',
    [Agent_SortField.CpuSpeed]: 'CPU Frequency',
    [Agent_SortField.CpuInfo]: 'Model Name', // not exists in linux
    [Agent_SortField.LicenseStatus]: 'Subscription', // not exists in linux
    [Agent_SortField.HelperCores]: 'Agent Name', // not exists in linux
    [Agent_SortField.Version]: 'Version',
    [Agent_SortField.UsedCpu]: 'CPU Load',
    [Agent_SortField.VmType]: 'VM Type',
    [Agent_SortField.Spot]: 'Spot',
    [Agent_SortField.HdType]: 'HD Type',
    [Agent_SortField.VolumeSize]: 'Volume Size',
    [Agent_SortField.CacheSize]: 'Cache Size',
    [Agent_SortField.Zone]: 'Availability Zone',
    [Agent_SortField.NumberOfCores]: 'Cloud Cores',
    [Agent_SortField.CacheSettingsDisplay]: 'Agent Name', // not exists in linux
    [Agent_SortField.Cluster]: 'Agent Name', // not exists in linux
  };
  const result: {
    [OsType.Windows]: MapAgentSortFieldToColumnsWindows;
    [OsType.Linux]: MapAgentSortFieldToColumnsLinux;
    [OsType.Mac]: MapAgentSortFieldToColumnsLinux;
  } = {
    [OsType.Windows]: agentSortFieldToColumnsWindows,
    [OsType.Linux]: agentSortFieldToColumnsLinux,
    [OsType.Mac]: agentSortFieldToColumnsLinux,
  };
  return result[os];
};

/* Creating a new object with the same keys as the `MAP_COLUMNS_To_Agent_SortField` object, but with the
values of the `SORTABLE_COLUMNS` object. */

const SORTABLE_COLUMNS = <T extends OsType>(os: T): SortableColumns[T] => {
  const sortableColumnsWindows: SortableWindowsColumns = {
    'Agent Name': { width: 360, minWidth: 120, name: 'Agent Name', sortable: true, locked: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Status: { width: 144, name: 'Status', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Agent Description': { width: 155, name: 'Agent Description', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Available Memory': { width: 155, name: 'Available Memory', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Build Group': { width: 130, name: 'Build Group', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Alerts: { width: 87, name: 'Alerts', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Available CPU': { width: 140, name: 'Available CPU', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Last Connected': { width: 155, name: 'Last Connected', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Logged On User': { width: 155, name: 'Logged On User', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Network: { width: 155, name: 'Network', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Used Helpers Cache': { width: 155, name: 'Used Helpers Cache', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Assignment Priority': { width: 160, name: 'Assignment Priority', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Registered Helper Cores': { width: 155, name: 'Registered Helper Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    IP: { width: 120, name: 'IP', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Up/Down time': { width: 155, name: 'Up/Down time', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Free Disk Space': { width: 155, name: 'Free Disk Space', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Build Priority': { width: 155, name: 'Build Priority', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Log Level': { width: 155, name: 'Log Level', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Build Cache Allocated': { width: 155, name: 'Build Cache Allocated', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending }, // (enabled yes/, sortable: trueo)
    'Operating System': { width: 176, name: 'Operating System', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'CPU Info': { width: 155, name: 'CPU Info', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Physical Cores': { width: 155, name: 'Physical Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Logical Cores': { width: 155, name: 'Logical Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    Port: { width: 155, name: 'Port', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Routing IP': { width: 155, name: 'Routing IP', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'MAC Address': { width: 155, name: 'MAC Address', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'VM Type': { width: 107, name: 'VM Type', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Spot: { width: 80, name: 'Spot', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'HD Type': { width: 105, name: 'HD Type', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Volume Size': { width: 130, name: 'Volume Size', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Cache Size': { width: 120, name: 'Cache Size', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Availability Zone': { width: 157, name: 'Availability Zone', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Cloud Cores': { width: 130, name: 'Cloud Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Build Cache Endpoint': { width: 155, name: 'Build Cache Endpoint', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Cache Cluster': { width: 160, name: 'Cache Cluster', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
  };
  const sortableColumnsLinux: SortableLinuxColumns = {
    'Agent Name': { width: 360, minWidth: 120, name: 'Agent Name', sortable: true, locked: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Agent Description': { width: 155, name: 'Agent Description', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Available Memory': { width: 155, name: 'Available Memory', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Build Group': { width: 130, name: 'Build Group', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Last Connected': { width: 155, name: 'Last Connected', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Operating System': { width: 176, name: 'Operating System', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Subscription: { width: 152, name: 'Subscription', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    IP: { width: 146, name: 'IP', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Status: { width: 144, name: 'Status', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Subscribed Cores/Total': { width: 144, name: 'Subscribed Cores/Total', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Initiator/Helper Active Cores': { width: 155, name: 'Initiator/Helper Active Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Assigned To Initiator': { width: 155, name: 'Assigned To Initiator', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'CPU Load': { width: 155, name: 'CPU Load', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'CPU Frequency': { width: 155, name: 'CPU Frequency', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Model Name': { width: 155, name: 'Model Name', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Version: { width: 155, name: 'Version', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'VM Type': { width: 107, name: 'VM Type', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    Spot: { width: 80, name: 'Spot', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'HD Type': { width: 105, name: 'HD Type', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Volume Size': { width: 130, name: 'Volume Size', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Cache Size': { width: 120, name: 'Cache Size', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
    'Availability Zone': { width: 157, name: 'Availability Zone', sortable: true, defaultSortOrder: Agent_SortOrder.Ascending },
    'Cloud Cores': { width: 130, name: 'Cloud Cores', sortable: true, defaultSortOrder: Agent_SortOrder.Descending },
  };
  const result: {
    [OsType.Windows]: SortableWindowsColumns;
    [OsType.Linux]: SortableLinuxColumns;
    [OsType.Mac]: SortableLinuxColumns;
  } = {
    [OsType.Windows]: sortableColumnsWindows,
    [OsType.Linux]: sortableColumnsLinux,
    [OsType.Mac]: sortableColumnsLinux,
  };
  return result[os];
};

const NON_SORTABLE_COLUMNS = <T extends OsType>(os: T): NonSortableColumns[T] => {
  const result: {
    [OsType.Windows]: NonSortableWindowsColumns;
    [OsType.Linux]: NonSortableLinuxColumns;
    [OsType.Mac]: NonSortableLinuxColumns;
  } = {
    [OsType.Windows]: {
      License: { width: 100, name: 'License' },
    },
    [OsType.Linux]: {},
    [OsType.Mac]: {},
  };
  return result[os];
};

// The default columns that are visible in the table in this exact order.
const DEFAULT_TABLE_COLUMNS = <T extends OsType>(os: T): DefaultTableColumns[T] => {
  const windowsSet: DefaultTableWindowsColumns = new Set([
    'Agent Name',
    'Status',
    'Agent Description',
    'Build Group',
    'Available CPU',
    'Last Connected',
    'Used Helpers Cache',
    'Registered Helper Cores',
  ]);
  const linuxSet: DefaultTableLinuxColumns = new Set([
    'Agent Name',
    'Status',
    'Agent Description',
    'Build Group',
    'Available Memory',
    'CPU Load',
    'Subscribed Cores/Total',
    'Subscription',
    'IP',
    'Assigned To Initiator',
    'Last Connected',
  ]);
  const result: {
    [OsType.Windows]: DefaultTableWindowsColumns;
    [OsType.Linux]: DefaultTableLinuxColumns;
    [OsType.Mac]: DefaultTableLinuxColumns;
  } = {
    [OsType.Windows]: windowsSet,
    [OsType.Linux]: linuxSet,
    [OsType.Mac]: linuxSet,
  };
  return result[os];
};
/* Creating a new array with the values of the `SORTABLE_COLUMNS` and `NON_SORTABLE_COLUMNS` objects. */
const NON_DEFAULT_COLUMNS = (os: OsType): TableColumns<OsType.Windows> | TableColumns<OsType.Linux> | TableColumns<OsType.Mac> => {
  const windowsResult = () => {
    const sortableColumns = SORTABLE_COLUMNS(OsType.Windows);
    const nonSortableColumns = NON_SORTABLE_COLUMNS(OsType.Windows);
    const defaultTableColumns = DEFAULT_TABLE_COLUMNS(OsType.Windows);
    return [...Object.values(sortableColumns).filter(column => !defaultTableColumns.has(column.name)), ...Object.values(nonSortableColumns).filter(column => !defaultTableColumns.has(column.name))];
  };
  const unixResult = () => {
    const sortableColumns = SORTABLE_COLUMNS(OsType.Linux);
    const defaultTableColumns = DEFAULT_TABLE_COLUMNS(OsType.Linux);
    return [...Object.values(sortableColumns).filter(column => !defaultTableColumns.has(column.name))];
  };
  if (os === OsType.Windows) {
    return windowsResult();
  }
  return unixResult();
};

const COLUMNS = (os: OsType) => {
  const sortableColumns = SORTABLE_COLUMNS(os);
  const nonSortableColumns = NON_SORTABLE_COLUMNS(os);
  const defaultColumns = Array.from(DEFAULT_TABLE_COLUMNS(os)).map(key =>
    sortableColumns[key as keyof typeof sortableColumns] ? sortableColumns[key as keyof typeof sortableColumns] : nonSortableColumns[key as keyof typeof nonSortableColumns],
  );

  return [...defaultColumns, ...NON_DEFAULT_COLUMNS(os)].sort((columnA, columnB) => Number(!!columnB.locked) - Number(!!columnA.locked));
};

const AGENTS_TABLE_WINDOWS_VIEWABLE_COLUMNS: AgentsTableColumnsWindowsManagement = new Set([
  'Agent Name',
  'Status',
  'Agent Description',
  'Build Group',
  'Available CPU',
  'Last Connected',
  'Up/Down time',
  'Logged On User',
  'Free Disk Space',
  'Used Helpers Cache',
  'Available Memory',
  'Build Priority',
  'Assignment Priority',
  'Log Level',
  'Build Cache Allocated',
  'Operating System',
  'CPU Info',
  'Physical Cores',
  'Logical Cores',
  'Registered Helper Cores',
  'Port',
  'Routing IP',
  'Network',
  'MAC Address',
  'IP',
  'VM Type',
  'Spot',
  'HD Type',
  'Volume Size',
  'Cache Size',
  'Availability Zone',
  'Cloud Cores',
  'Build Cache Endpoint',
  'Cache Cluster',
]);
const AGENTS_TABLE_LINUX_VIEWABLE_COLUMNS: AgentsTableColumnsLinuxManagement = new Set([
  'Agent Name',
  'Available Memory',
  'Agent Description',
  'Build Group',
  'Status',
  'IP',
  'Operating System',
  'Last Connected',
  'Subscription',
  'Subscribed Cores/Total',
  'Initiator/Helper Active Cores',
  'CPU Load',
  'CPU Frequency',
  'Model Name',
  'Version',
  'Assigned To Initiator',
  'VM Type',
  'Spot',
  'HD Type',
  'Volume Size',
  'Cache Size',
  'Availability Zone',
  'Cloud Cores',
]);

export const CLOUD_TAG_AGENT_TABLE_COLUMNS: string[] = ['Availability Zone', 'Cache Size', 'Volume Size', 'HD Type', 'Spot', 'VM Type', 'Cloud Cores'];

export const useAgentsTableConstantsProvider = () => {
  const osService = useOsService();
  const os = computed(() => osService?.osType.value);
  const columns = computed(() => (os.value ? COLUMNS(os.value) : []));
  const viewableColumns = computed(() => {
    const result = {
      [OsType.Windows]: AGENTS_TABLE_WINDOWS_VIEWABLE_COLUMNS,
      [OsType.Linux]: AGENTS_TABLE_LINUX_VIEWABLE_COLUMNS,
      [OsType.Mac]: AGENTS_TABLE_LINUX_VIEWABLE_COLUMNS,
    };
    return os.value ? result[os.value] : new Set();
  });
  const defaultColumns = computed(() => (os.value ? DEFAULT_TABLE_COLUMNS(os.value) : new Set())) as ComputedRef<ReturnType<typeof DEFAULT_TABLE_COLUMNS>>;
  const sortableColumns = computed(() => (os.value ? SORTABLE_COLUMNS(os.value) : {})) as ComputedRef<ReturnType<typeof SORTABLE_COLUMNS>>;
  const mapAgentSortFieldToColumns = computed(() => (os.value ? MAP_Agent_SortField_To_COLUMNS(os.value) : {})) as ComputedRef<ReturnType<typeof MAP_Agent_SortField_To_COLUMNS>>;
  const mapColumnsToAgentSortField = computed(() => (os.value ? MAP_COLUMNS_To_Agent_SortField(os.value) : {})) as ComputedRef<ReturnType<typeof MAP_COLUMNS_To_Agent_SortField>>;
  app.provide(IKAgentsTableConstants, {
    columns,
    viewableColumns,
    defaultColumns,
    sortableColumns,
    mapAgentSortFieldToColumns,
    mapColumnsToAgentSortField,
  });
  return {
    columns,
    viewableColumns,
    defaultColumns,
    sortableColumns,
    mapAgentSortFieldToColumns,
    mapColumnsToAgentSortField,
  };
};

export const useAgentsTableConstants = () => {
  const agentsTableConstants = inject(IKAgentsTableConstants);
  if (!agentsTableConstants) {
    throw new Error('useAgentsTableConstants must be used within a useAgentsTableConstantsProvider');
  }
  return agentsTableConstants;
};
